/**
 * Der Endpunkt-Dialog: Name, Adresse, Token und `max_parallel_calls`.
 *
 * **Das Token wird geschrieben und nie zurückgelesen.** Diese Maske zeigt keines
 * an, auch kein verkürztes — sie bekommt gar keines geliefert. Beim Ändern bleibt
 * das Feld deshalb leer, und wer es leer lässt, lässt das hinterlegte Token
 * stehen.
 *
 * `max_parallel_calls` begrenzt, was der Server insgesamt gleichzeitig sieht
 * (ADR-0003); der Startwert für `ai.yq-os.com` ist 2.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import {
  Button,
  Chip,
  DataTable,
  Dialog,
  FormGrid,
  FormRow,
  Modal,
  TextField,
} from "../../design-system";
import type { AdminApi, Endpoint, EndpointDraft } from "../../api/client";
import { NumericCell, StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";

export function EndpointsSection({ client }: { client: AdminApi }): ReactElement {
  const { state, reload } = useLoaded(() => client.endpoints());
  const [edited, setEdited] = useState<Endpoint | "neu" | null>(null);

  return (
    <>
      <ListSection
        title="Endpunkte"
        glyph="network"
        first
        headerRight={
          // Der Knopf zum Anlegen stand am Fuß der Karte; ein Abschnitt hat
          // keinen Fuß, und ein Schalter über die ganze Fensterbreite wäre
          // keiner. Er steht deshalb neben dem Abzeichen im Kopf — dort, wo das
          // Design-System die Knöpfe eines Abschnitts führt.
          <span style={{ display: "flex", alignItems: "center", gap: "var(--sp-5)" }}>
            {state.kind === "loaded" ? <Chip>{`${state.value.length} angelegt`}</Chip> : null}
            <Button variant="outline" icon="plus" onClick={() => setEdited("neu")}>
              Endpunkt anlegen
            </Button>
          </span>
        }
      >
        <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {state.kind === "loaded" && state.value.length === 0 ? (
            <Notice text="Kein Endpunkt angelegt." />
          ) : null}
        </div>

        {state.kind === "loaded" && state.value.length > 0 ? (
          <DataTable
            onRowClick={(_row, index) => setEdited(state.value[index] ?? null)}
            columns={[
              { key: "name", label: "Name", width: 220, maxWidth: 220 },
              // Eine Adresse wird von vorn gelesen und bricht hinten ab; ganz
              // steht sie im Titel der Zelle.
              { key: "address", label: "Adresse", width: 420, maxWidth: 420 },
              { key: "maxParallelCalls", label: "Gleichzeitige Aufrufe", align: "right", width: 160 },
            ]}
            rows={state.value.map((endpoint) => ({
              cells: {
                name: <StrongCell>{endpoint.name}</StrongCell>,
                address: <span title={endpoint.address}>{endpoint.address}</span>,
                maxParallelCalls: <NumericCell>{endpoint.maxParallelCalls}</NumericCell>,
              },
            }))}
          />
        ) : null}
      </ListSection>

      {edited === null ? null : (
        <EndpointDialog
          endpoint={edited === "neu" ? null : edited}
          save={(draft) =>
            edited === "neu"
              ? client.createEndpoint(draft)
              : client.updateEndpoint(edited.id, draft)
          }
          close={() => setEdited(null)}
          saved={() => {
            setEdited(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function EndpointDialog({
  endpoint,
  save,
  close,
  saved,
}: {
  endpoint: Endpoint | null;
  save: (draft: EndpointDraft) => Promise<Endpoint>;
  close: () => void;
  saved: () => void;
}): ReactElement {
  const [name, setName] = useState(endpoint?.name ?? "");
  const [address, setAddress] = useState(endpoint?.address ?? "");
  const [token, setToken] = useState("");
  const [calls, setCalls] = useState(String(endpoint?.maxParallelCalls ?? 2));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    setBusy(true);
    setError(null);

    save({
      name,
      address,
      // Leer heißt „unverändert" — und beim Anlegen weist der Server es ab.
      token: token === "" ? null : token,
      maxParallelCalls: Number(calls),
    }).then(saved, (failure: unknown) => {
      setBusy(false);
      setError(describe(failure));
    });
  };

  return (
    <Modal onClose={close}>
      <Dialog
        title={endpoint === null ? "Endpunkt anlegen" : "Endpunkt ändern"}
        description="Mehrere Agenten nutzen denselben Endpunkt. Das Token wird geschrieben und nie wieder angezeigt."
        width={520}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
              {busy ? "Wird gespeichert ..." : "Speichern"}
            </Button>
          </>
        }
      >
        <FormGrid labelWidth="var(--grid-label-col)">
          <FormRow label="Name" htmlFor="endpoint-name">
            <TextField
              id="endpoint-name"
              value={name}
              onChange={setName}
              placeholder="yQ AI"
            />
          </FormRow>

          <FormRow label="Adresse" htmlFor="endpoint-address">
            <TextField
              id="endpoint-address"
              value={address}
              onChange={setAddress}
              placeholder="https://ai.yq-os.com"
            />
          </FormRow>

          <FormRow label="Token" htmlFor="endpoint-token">
            <TextField
              id="endpoint-token"
              type="password"
              value={token}
              onChange={setToken}
              placeholder={
                endpoint === null ? "Token des Dienstkontos" : "Leer lassen: bleibt unverändert"
              }
            />
          </FormRow>

          <FormRow label="Gleichzeitige Aufrufe" htmlFor="endpoint-calls">
            <TextField
              id="endpoint-calls"
              type="number"
              value={calls}
              onChange={setCalls}
            />
          </FormRow>

          {error === null ? null : (
            <FormRow full>
              <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
            </FormRow>
          )}
        </FormGrid>
      </Dialog>
    </Modal>
  );
}
